// Dependencies
import { isAfter, isBefore, subYears } from 'date-fns';
import { createSlice, createAction } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
// Types
import { List } from '../objects/list';
import { Error } from '../objects/error';
import { Column } from '../objects/column';
import { Preferences } from '../../profile/objects/preferences';
import { DestinationReduced } from '../../destination/objects/destinationReduced';
import { VehicleModelState } from '../objects/vehicleModelState';
import { VehicleModel } from '../objects/vehicleModel';
import { VehicleModelCategory } from '../objects/vehicleModelCategory';
import { VehicleModelType } from '../objects/vehicleModelType';
import { Picture } from '../../picture/objects/picture';

const setPicture = createAction<boolean | Picture>('PICTURE_SET_PICTURE');
const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const addCustomPictures = createAction<Picture[]>('PICTURE_ADD_CUSTOM_PICTURES');

const defaultColumn: Column[] = [{
    label: 'name',
    field_name: 'NAME',
    checked: true,
    order_by: 'title',
    fixed_width: false,
    last_updated: false
}, {
    label: 'category',
    field_name: 'VEHICLE_CATEGORY',
    checked: false,
    order_by: '',
    fixed_width: false,
    last_updated: false
}, {
    label: 'type',
    field_name: 'VEHICLE_TYPE',
    checked: false,
    order_by: '',
    fixed_width: false,
    last_updated: false
}, {
    label: 'max-passengers',
    field_name: 'MAX_PASSENGERS',
    checked: false,
    order_by: 'max_passengers',
    fixed_width: true,
    last_updated: false
}, {
    label: 'nb-baggage',
    field_name: 'NB_BAGGAGE',
    checked: false,
    order_by: 'nb_baggage',
    fixed_width: true,
    last_updated: false
}, {
    label: 'online-to',
    field_name: 'VISIBILITY',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false
}, {
    label: 'online-agency',
    field_name: 'AGENCY_VISIBILITY',
    checked: true,
    order_by: '',
    fixed_width: true,
    last_updated: false
}, {
    label: 'created-date',
    field_name: 'CREATED_DATE',
    checked: false,
    order_by: 'created_date',
    fixed_width: true,
    last_updated: false
}, {
    label: 'created-by',
    field_name: 'CREATED_BY',
    checked: false,
    order_by: 'creator__username',
    fixed_width: true,
    last_updated: false
}, {
    label: 'modified-date',
    field_name: 'MODIFIED_DATE',
    checked: true,
    order_by: 'modified_date',
    fixed_width: true,
    last_updated: false
}, {
    label: 'modified-by',
    field_name: 'MODIFIED_BY',
    checked: true,
    order_by: 'last_author__username',
    fixed_width: true,
    last_updated: false
}];

// const subDomain = window.location.host.split('.')[0];
const subDomain = 'verdie-voyages';

const initialState: VehicleModelState = {
    acriss: '',
    acriss_error: '',
    cancel_modal: false,
    columns: defaultColumn,
    count: 0,
    create: false,
    description_locales: {},
    destination: null,
    destination_error: '',
    edit: false,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_max_passengers: 0,
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_nb_baggage: 0,
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    filter_status: 'ALL',
    filter_vehicle_category: 'ALL',
    filter_vehicle_type: 'ALL',
    limit: 50,
    list: null,
    name_locales: {},
    nb_doors: null,
    nb_doors_error: '',
    order: 'asc',
    order_by: 'title',
    page: 0,
    pictures: [],
    pictures_order: false,
    preferences: [],
    refresh: false,
    running: false,
    search: '',
    title: '',
    title_error: '',
    transmission: '',
    transmission_error: '',
    vehicle_category: '',
    vehicle_category_error: '',
    vehicle_model: null,
    vehicle_type: '',
    vehicle_type_error: '',
    visibility: 0,
};

const vehicleModelSlice = createSlice({
    name: 'vehicle_model',
    initialState,
    reducers: {
        addPreferences: (state, action: PayloadAction<Preferences[]>) => {
            state.preferences = [...state.preferences, ...action.payload];
        },
        deletePicture: (state, action: PayloadAction<number>) => {
            const pictures = [...state.pictures];
            pictures.splice(action.payload, 1);
            state.pictures = pictures;
        },
        moveDownColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = {...columns[action.payload + 1]};
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        moveUpColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = {...columns[action.payload - 1]};
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        resetFilters: (state) => {
            state.filter_created_date_gte = null;
            state.filter_created_date_lte = null;
            state.filter_creator = '';
            state.filter_last_author = '';
            state.filter_max_passengers = 0;
            state.filter_modified_date_gte = null;
            state.filter_modified_date_lte = null;
            state.filter_nb_baggage = 0;
            state.filter_period = 'CREATION';
            state.filter_period_type = 0;
            state.filter_status = 'ALL';
            state.filter_vehicle_category = 'ALL';
            state.filter_vehicle_type = 'ALL';
        },
        resetVehicleModel: (state) => {
            state.acriss = '';
            state.acriss_error = '';
            state.cancel_modal = false;
            state.create = false;
            state.description_locales = {};
            state.edit = false;
            state.name_locales = {};
            state.nb_doors = null;
            state.nb_doors_error = '';
            state.pictures = [];
            state.pictures_order = false;
            state.vehicle_category = '';
            state.vehicle_category_error = '';
            state.visibility = 0;
            state.transmission = '';
            state.transmission_error = '';
        },
        toggleEdit: (state) => {
            state.edit = !state.edit;
            if (state.edit && state.vehicle_model !== null) {
                /** @todo: init the default values for edition */            }
        },
        setErrors: (state, action: PayloadAction<Error[]>) => {
            const errors = action.payload;
            errors.map((error: Error) => {
                state[error.key] = error.error;
            });
        },
        setExportType: (state, action: PayloadAction<'CSV' | 'PDF'>) => {
            state.export_type = action.payload;
        },
        setLimit: (state, action: PayloadAction<number>) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action: PayloadAction<List>) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setOrderBy: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.order_by = action.payload;
            state.order = state.order_by === action.payload && state.order === 'asc' ? 'desc': 'asc';
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.list = null;
            state.page = action.payload;
        },
        setRunning: (state, action: PayloadAction<boolean>) => {
            state.running = action.payload;
        },
        setSearch: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        setVehicleModel: (state, action: PayloadAction<VehicleModel>) => {
            state.edit = false;
            state.vehicle_model = action.payload;
        },
        setVehicleModelPreferences: (state, action: PayloadAction<Preferences[]>) => {
            state.preferences = action.payload;
        },
        toggleColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            const column = {...columns[action.payload]};
            column.checked = !column.checked;
            columns[action.payload] = column;
            state.columns = columns;
        },
        toggleCancelModal: (state) => {
            state.cancel_modal = !state.cancel_modal;
        },
        toggleCreate: (state) => {
            state.create = !state.create;
        },
        toggleExport: (state, action: PayloadAction<boolean>) => {
            state.export_list = action.payload;
        },
        toggleExportFull: (state) => {
            state.export_full = !state.export_full;
        },
        toggleFilters: (state) => {
            state.filter_open = !state.filter_open;
        },
        togglePicturesOrder: (state) => {
            state.pictures_order = !state.pictures_order;
        },
        toggleRefresh: (state) => {
            state.refresh = !state.refresh;
        },
        updateAcriss: (state, action: PayloadAction<string>) => {
            state.acriss = action.payload;
            state.acriss_error = '';
        },
        updateDescriptionLocale: (state, action: PayloadAction<{id: number, value: string}>) => {
            const { id, value } = action.payload;
            const description_locales = {...state.description_locales};
            description_locales[id] = value;
            state.description_locales = description_locales;
        },
        updateFilterCreatedDateGte: (state, action: PayloadAction<null | Date>) => {
            state.filter_created_date_gte = action.payload;
        },
        updateFilterCreatedDateLte: (state, action: PayloadAction<null | Date>) => {
            state.filter_created_date_lte = action.payload;
        },
        updateFilterCreator: (state, action: PayloadAction<string>) => {
            state.filter_creator = action.payload;
        },
        updateFilterDestination: (state, action: PayloadAction<null | DestinationReduced>) => {
            state.filter_destination = action.payload;
        },
        updateFilterLastAuthor: (state, action: PayloadAction<string>) => {
            state.filter_last_author = action.payload;
        },
        updateFilterMaxPassengers: (state, action: PayloadAction<number>) => {
            state.filter_max_passengers = action.payload;
        },
        updateFilterModifiedDateGte: (state, action: PayloadAction<null | Date>) => {
            state.filter_modified_date_gte = action.payload;
        },
        updateFilterModifiedDateLte: (state, action: PayloadAction<null | Date>) => {
            state.filter_modified_date_lte = action.payload;
        },
        updateFilterNbBaggage: (state, action: PayloadAction<number>) => {
            state.filter_nb_baggage = action.payload;
        },
        updateFilterPeriod: (state, action: PayloadAction<'CREATION' | 'MODIFICATION'>) => {
            state.filter_period = action.payload;
            state.filter_period_type = 0;
            state.filter_created_date_gte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_gte;
            state.filter_created_date_lte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_lte;
            state.filter_modified_date_gte = action.payload === 'CREATION' ? null : state.filter_modified_date_gte;
            state.filter_modified_date_lte = action.payload === 'CREATION' ? null : state.filter_modified_date_lte;
        },
        updateFilterPeriodType: (state, action: PayloadAction<number>) => {
            state.filter_period_type = action.payload;
        },
        updateFilterStatus: (state, action: PayloadAction<'ALL' | 'OFFLINE' | 'ONLINE_TO' | 'ONLINE_AGENCY'>) => {
            state.filter_status = action.payload;
        },
        updateFilterVehicleCategory: (state, action: PayloadAction<'ALL' | VehicleModelCategory>) => {
            state.filter_vehicle_category = action.payload;
            switch(action.payload){
                case 'MOTO':
                    state.filter_vehicle_type = 'MOTO';
                    break;
                case 'ALL':
                default:
                    state.filter_vehicle_type = 'ALL';
                    break;
            }
        },
        updateFilterVehicleType: (state, action: PayloadAction<'ALL' | VehicleModelType>) => {
            state.filter_vehicle_type = action.payload;
        },
        updateListItemVisibility: (state, action: PayloadAction<VehicleModel>) => {
            if (state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === action.payload.id) {
                        list[i].visibility = action.payload.visibility;
                        break;
                    }
                }
                state.list = list;
            }
        },
        updateNameLocale: (state, action: PayloadAction<{id: number, value: string}>) => {
            console.log('action',   action);
            const { id, value } = action.payload;
            const name_locales = {...state.name_locales};
            name_locales[id] = value;
            state.name_locales = name_locales;
        },
        updateNbDoors: (state, action: PayloadAction<null | number>) => {
            state.nb_doors = action.payload;
            state.nb_doors_error = '';
        },
        updatePictureOrder: (state, action: PayloadAction<{ drag_index: number, hover_index: number }>) => {
            const { drag_index, hover_index } = action.payload;
            const pictures = [...state.pictures];
            pictures.splice(drag_index, 1);
            pictures.splice(hover_index, 0, state.pictures[drag_index]);
            state.pictures = pictures;
        },
        updateTitle: (state, action: PayloadAction<string>) => {
            state.title = action.payload;
        },
        updateTransmission: (state, action: PayloadAction<'' | 'manual' | 'automatic'>) => {
            state.transmission = action.payload;
            state.transmission_error = '';
        },
        updateVehicleCategory: (state, action: PayloadAction<'' | VehicleModelCategory>) => {
            state.vehicle_category = action.payload;
            switch(action.payload){
                case 'MOTO':
                    state.vehicle_type = 'MOTO';
                    break;
                case '':
                default:
                    state.vehicle_type = state.vehicle_model === null ? '' : state.vehicle_model?.vehicle_type;
                    break;
            }
            state.vehicle_category_error = '';
            if(action.payload !== 'CAR') {
                state.acriss = '';
                state.acriss_error = '';
                state.nb_doors = null;
                state.nb_doors_error = '';
                state.transmission = '';
                state.transmission_error = '';
            } else if(state.vehicle_model !== null) {
                state.acriss = state.vehicle_model.acriss ?? '';
                state.nb_doors = state.vehicle_model.nb_doors;
                state.transmission = state.vehicle_model.transmission;
            }
        },
        updateVehicleType: (state, action: PayloadAction<'' | VehicleModelType>) => {
            state.vehicle_type = action.payload;
            state.vehicle_type_error = '';
        },
        updateVisibility: (state, action: PayloadAction<number>) => {
            state.visibility = action.payload;
        },
        setColumns: (state, action: PayloadAction<Column[]>) => {
            state.columns = action.payload
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(addCustomPictures, (state, action) => {
                if (state.vehicle_model !== null || state.create) {
                    state.pictures = [...state.pictures, ...action.payload];
                }
            })
            .addCase(setPicture, (state, action) => {
                if (action.payload !== true && action.payload !== false) {
                    const pictures = [...state.pictures];
                    for (let i = 0; i < pictures.length; i++) {
                        if (pictures[i].id === action.payload.id) {
                            pictures[i] = {...action.payload};
                            break;
                        }
                    }
                    state.pictures = pictures;
                }
            })
            .addCase(setPreferences, (state, action) => {
                const field_name: string[] = [];
                const columns: Column[] = [];
                const preferences: Preferences[] = [];
                let order_by = '-created_date';
                let order: 'asc' | 'desc' = 'asc';
                action.payload.map((preference: Preferences) => {
                    if (preference.module === 'VEHICLE_MODEL') {
                        if (!field_name.includes(preference.field_name)) {
                            switch (preference.field_name) {
                                case 'NAME':
                                    columns.push({label: 'name', field_name: preference.field_name, checked: preference.active, order_by: 'title', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'name' : order_by;
                                    break;
                                case 'VEHICLE_CATEGORY':
                                    columns.push({label: 'category', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'vehicle_category' : order_by;
                                    break;
                                case 'VEHICLE_TYPE':
                                    columns.push({label: 'type', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'vehicle_type' : order_by;
                                    break;
                                case 'MAX_PASSENGERS':
                                    columns.push({label: 'max-passengers', field_name: preference.field_name, checked: preference.active, order_by: 'max_passengers', fixed_width: true, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'max_passengers' : order_by;
                                    break;
                                case 'NB_BAGGAGE':
                                    columns.push({label: 'nb-baggage', field_name: preference.field_name, checked: preference.active, order_by: 'nb_baggage', fixed_width: true, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'nb_baggage' : order_by;
                                    break;
                                case 'VISIBILITY':
                                    columns.push({label: 'online-to', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: true, last_updated: false});
                                    break;
                                case 'AGENCY_VISIBILITY':
                                    columns.push({label: 'online-agency', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: true, last_updated: false});
                                    break;
                                case 'CREATED_DATE':
                                    columns.push({label: 'created-date', field_name: preference.field_name, checked: preference.active, order_by: 'created_date', fixed_width: true, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
                                    break;
                                case 'CREATED_BY':
                                    columns.push({label: 'created-by', field_name: preference.field_name, checked: preference.active, order_by: 'creator__username', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
                                    break;
                                case 'MODIFIED_DATE':
                                    columns.push({label: 'modified-date', field_name: preference.field_name, checked: preference.active, order_by: 'modified_date', fixed_width: true, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
                                    break;
                                case 'MODIFIED_BY':
                                    columns.push({label: 'modified-by', field_name: preference.field_name, checked: preference.active, order_by: 'last_author__username', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
                                    break;
                                default:
                                    break;
                            }
                            preferences.push(preference);
                            field_name.push(preference.field_name);
                            order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
                        }
                    }
                });
                if (columns.length !== state.columns.length) {
                    state.columns.map(column => {
                        let added = false;
                        for (let i = 0; i < columns.length; i++) {
                            if (columns[i].label === column.label) {
                                added = true;
                                break;
                            }
                        }
                        if (!added) {
                            columns.push({...column});
                        }
                    });
                }
                state.preferences = preferences;
                state.columns = columns;
                state.order_by = order_by;
                state.order = order;
            })
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .addDefaultCase(() => {});
    }
});

export const {
    addPreferences,
    deletePicture,
    moveDownColumn,
    moveUpColumn,
    resetFilters,
    resetVehicleModel,
    setErrors,
    setExportType,
    setLimit,
    setList,
    setOrderBy,
    setPage,
    setRunning,
    setSearch,
    setVehicleModel,
    setVehicleModelPreferences,
    toggleCancelModal,
    toggleColumn,
    toggleCreate,
    toggleEdit,
    toggleExport,
    toggleExportFull,
    toggleFilters,
    togglePicturesOrder,
    toggleRefresh,
    updateAcriss,
    updateDescriptionLocale,
    updateFilterCreatedDateGte,
    updateFilterCreatedDateLte,
    updateFilterCreator,
    updateFilterDestination,
    updateFilterLastAuthor,
    updateFilterMaxPassengers,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterNbBaggage,
    updateFilterPeriod,
    updateFilterPeriodType,
    updateFilterStatus,
    updateFilterVehicleCategory,
    updateFilterVehicleType,
    updateListItemVisibility,
    updateNameLocale,
    updateNbDoors,
    updatePictureOrder,
    updateTitle,
    updateTransmission,
    updateVehicleCategory,
    updateVehicleType,
    updateVisibility,
    setColumns
} = vehicleModelSlice.actions;

export default vehicleModelSlice;